import type { QuestionCategory, QuestionType } from "@/lib/constants";

export type StartResponse = {
  startedAt: string;
  maxDurationSec: number;
  candidateName: string;
  language?: string;
  questions: ClientQuestion[];
};

export type ClientQuestion = {
  id: string;
  order_index: number;
  category: QuestionCategory;
  type: QuestionType;
  prompt: string;
  options: { value: string; label: string }[] | null;
  time_limit_sec: number;
  skippable: boolean;
};

const base = (token: string) => `/api/interview/${token}`;

export async function startInterview(token: string): Promise<StartResponse> {
  const res = await fetch(`${base(token)}/start`, { method: "POST" });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg =
      body.error ||
      (res.status === 504 || res.status === 408
        ? "Interview prep timed out. Please try again in a moment."
        : "Failed to start interview");
    throw new Error(msg);
  }
  return res.json();
}

export async function submitAnswer(
  token: string,
  data: { questionId: string; answer?: string | null; skipped?: boolean },
) {
  await fetch(`${base(token)}/answer`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
}

export async function sendEvent(
  token: string,
  type: string,
  payload?: Record<string, unknown>,
): Promise<{ violationCount?: number; flagged?: boolean }> {
  const res = await fetch(`${base(token)}/event`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ type, payload }),
    keepalive: true,
  });
  return res.json().catch(() => ({}));
}

export async function uploadMedia(
  token: string,
  kind:
    | "snapshot"
    | "violation_snapshot"
    | "identity_photo"
    | "identity_voice"
    | "live",
  blob: Blob,
) {
  const fd = new FormData();
  fd.append("kind", kind);
  fd.append("file", blob, kind);
  await fetch(`${base(token)}/media`, { method: "POST", body: fd });
}

export async function finishInterview(token: string) {
  const res = await fetch(`${base(token)}/finish`, { method: "POST" });
  return res.json().catch(() => ({}));
}
